/**
 * Donorfy Credential Form
 *
 * Form for configuring Donorfy API credentials with:
 * - Tenant ID (organization identifier in the URL)
 * - Username for Basic Authentication
 * - Password for Basic Authentication
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface DonorfyFormData {
  tenantId: string;
  username: string;
  password: string;
}

interface DonorfyCredentialFormProps {
  formData: DonorfyFormData;
  onChange: (data: Partial<DonorfyFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; data?: any; details?: any } | null;
  testing?: boolean;
  onTest?: () => Promise<void>;
}

export function DonorfyCredentialForm({
  formData,
  onChange,
  isEditing = false,
}: DonorfyCredentialFormProps) {
  const handleChange = (field: keyof DonorfyFormData, value: string) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Tenant ID */}
      <div className="space-y-2">
        <Label htmlFor="tenantId" className="text-sm font-medium">
          Tenant ID <span className="text-red-500">*</span>
        </Label>
        <Input
          id="tenantId"
          placeholder="e.g., ABCD1234XY"
          value={formData.tenantId || ''}
          onChange={(e) => handleChange('tenantId', e.target.value)}
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Your Donorfy organization ID (found in API URLs, e.g., data.donorfy.com/api/v1/<strong>YOUR_TENANT_ID</strong>/...)
        </p>
      </div>

      {/* Username */}
      <div className="space-y-2">
        <Label htmlFor="username" className="text-sm font-medium">
          Username <span className="text-red-500">*</span>
        </Label>
        <Input
          id="username"
          placeholder="Your Donorfy username"
          value={formData.username || ''}
          onChange={(e) => handleChange('username', e.target.value)}
          className="text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Your Donorfy username for Basic Authentication
        </p>
      </div>

      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm font-medium">
          Password <span className="text-red-500">*</span>
        </Label>
        <Input
          id="password"
          type="password"
          placeholder={isEditing ? "Leave unchanged or enter new password" : "Enter your Donorfy password"}
          value={formData.password || ''}
          onChange={(e) => handleChange('password', e.target.value)}
          autoComplete="new-password"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Your Donorfy password for Basic Authentication
        </p>
      </div>
    </div>
  );
}

export default DonorfyCredentialForm;
